import React, { useState, useEffect, useRef, useCallback } from 'react';
import { HiOutlineRefresh, HiOutlineSearch, HiOutlineCamera, HiOutlineCheckCircle, HiOutlineTruck, HiOutlineEye, HiOutlineLocationMarker } from 'react-icons/hi';
import { FaMapMarkerAlt } from 'react-icons/fa';
import axiosInstance from '../../api/axiosConfig';
import { useAuth } from '../../context/AuthContext';
import ProofUploadModal from './ProofUploadModal';
import ImageViewer from './ImageViewer';
import ProofPlaceholder from '../common/ProofPlaceholder';
import DeliveryIndicators, { hasProof, STATUS_OPTIONS } from './DeliveryIndicators';
import { onSocketEvent, onSocketReconnect, onAppVisible } from '../../realtime/socketClient';
import './MyOrders.css';

const PAGE_SIZE = 20;

// Branches where the office closes out deliveries and invoice status itself —
// the delivery boy there only uploads proof. Matched by branch CODE, not
// name: codes ("AHM-MAIN") are the stable identifier convention this codebase
// already uses elsewhere (e.g. salesman codes like "B1645"), so this survives
// a display-name edit even though tenant rename isn't wired up today. The
// "Ahlamart" company's one branch, "Ahlamart - Main", has code "AHM-MAIN".
// Add a branch code here to extend the restriction; no other logic changes.
const RESTRICTED_ACTION_BRANCH_CODES = ['AHM-MAIN'];

const MyOrders = ({ isDeliveryBoy, scopeSelector = null, scope = null }) => {
    const { user } = useAuth();
    const hideDeliveryBoyActions = RESTRICTED_ACTION_BRANCH_CODES.includes(user?.branch?.code);
    const [orders, setOrders] = useState([]);
    const [loading, setLoading] = useState(true);
    const [firstLoad, setFirstLoad] = useState(true);
    const [searchTerm, setSearchTerm] = useState('');
    // The term actually sent to the server, 500ms behind what is being typed.
    const [debouncedSearch, setDebouncedSearch] = useState('');
    const [statusFilter, setStatusFilter] = useState('');
    // The two indicator filters. Independent of the status tab and of each
    // other — all three compose server-side, so "Delivered + invoice pending"
    // is one click each rather than a status that has to be invented.
    const [proofFilter, setProofFilter] = useState(false);
    const [invoiceFilter, setInvoiceFilter] = useState(false);
    // Status filtering, searching and paging are all done by the server. Doing
    // any of it here would only ever see the current page — which is exactly how
    // "In Transit (0)" happened while In Transit orders waited on page 2.
    const [page, setPage] = useState(1);
    const [pageCount, setPageCount] = useState(0);
    const [total, setTotal] = useState(0);
    const [statusCounts, setStatusCounts] = useState({});
    const [showProofModal, setShowProofModal] = useState(false);
    const [selectedOrder, setSelectedOrder] = useState(null);
    const [viewingImage, setViewingImage] = useState(null);
    const [liveLocationInputs, setLiveLocationInputs] = useState({}); // Track live location inputs per order
    const [liveLocationErrors, setLiveLocationErrors] = useState({}); // Track validation errors per order
    const [deliveryBoyNumbers, setDeliveryBoyNumbers] = useState({}); // Track delivery boy numbers per order

    // Rising counter: only the newest request is allowed to write to state, so
    // quickly tapping between tabs can't leave a slower earlier response on
    // screen — which would look exactly like the filter being ignored.
    const requestId = useRef(0);

    // System-admin company/branch scope, honoured server-side by
    // applyAdminTenantOverride. Undefined (not sent) for everyone else.
    const scopeCompany = scope?.company || undefined;
    const scopeBranch = scope?.branch || undefined;

    const fetchOrders = useCallback(async () => {
        const myRequest = ++requestId.current;
        try {
            setLoading(true);
            const response = await axiosInstance.get('/api/deliveries', {
                params: {
                    status: statusFilter || undefined,
                    hasProof: proofFilter ? 'true' : undefined,
                    invoicePending: invoiceFilter ? 'true' : undefined,
                    search: debouncedSearch || undefined,
                    company: scopeCompany,
                    branch: scopeBranch,
                    page,
                    limit: PAGE_SIZE,
                    withCounts: 1
                }
            });
            if (myRequest !== requestId.current) return; // superseded
            if (response.data.success) {
                setOrders(response.data.data);
                setTotal(response.data.total || 0);
                setPageCount(response.data.pages || 0);
                if (response.data.statusCounts) setStatusCounts(response.data.statusCounts);
            }
        } catch (error) {
            if (myRequest !== requestId.current) return;
            console.error('Error fetching orders:', error);
            alert('Failed to fetch orders');
        } finally {
            if (myRequest === requestId.current) {
                setLoading(false);
                setFirstLoad(false);
            }
        }
    }, [statusFilter, proofFilter, invoiceFilter, debouncedSearch, page, scopeCompany, scopeBranch]);

    // A changed company/branch scope restarts at page 1, so the admin doesn't
    // land on an out-of-range page of the newly narrowed list.
    useEffect(() => {
        setPage(1);
    }, [scopeCompany, scopeBranch]);

    // Typing waits 500ms; changing the search always restarts at page 1. Both
    // land in one state update so only a single fetch follows. Stored trimmed so
    // adding a trailing space isn't treated as a new query.
    useEffect(() => {
        const timer = setTimeout(() => {
            setDebouncedSearch(searchTerm.trim());
            setPage(1);
        }, 500);
        return () => clearTimeout(timer);
    }, [searchTerm]);

    // The one place orders are loaded. Tab and page changes are immediate.
    useEffect(() => {
        fetchOrders();
    }, [fetchOrders]);

    // Recovery: a missed 'delivery:assigned' socket/push event (driver was
    // offline, the tab was closed, push permission was never granted) must
    // not mean the delivery stays invisible — the database is what
    // fetchOrders reads, so all three of these triggers just re-run the SAME
    // fetch the mount effect above already does. Driver-only: an admin's own
    // scoped "My Deliveries" view has nothing to recover here, since
    // delivery:assigned only ever targets a delivery_boy's own socket room.
    // fetchOrders already guards against a stale response racing a newer one
    // (the requestId ref inside it), so firing this on top of an in-flight
    // fetch is safe.
    useEffect(() => {
        if (!isDeliveryBoy) return undefined;

        const offAssigned = onSocketEvent('delivery:assigned', fetchOrders);
        const offReconnect = onSocketReconnect(fetchOrders);
        const offVisible = onAppVisible(fetchOrders);

        return () => {
            offAssigned();
            offReconnect();
            offVisible();
        };
    }, [isDeliveryBoy, fetchOrders]);

    const handleStatusFilter = (status) => {
        setStatusFilter(status);
        setPage(1);
    };

    // Toggling a chip restarts at page 1 for the same reason a tab does — the
    // narrowed list is shorter, so the current page may not exist any more.
    const toggleProofFilter = () => { setProofFilter(v => !v); setPage(1); };
    const toggleInvoiceFilter = () => { setInvoiceFilter(v => !v); setPage(1); };

    const handleStartDelivery = async (orderId) => {
        try {
            const liveLocationUrl = liveLocationInputs[orderId] || '';
            const deliveryBoyNumber = deliveryBoyNumbers[orderId] || '';
            
            // Validate URL if provided
            if (liveLocationUrl.trim() && !isValidUrl(liveLocationUrl.trim())) {
                alert('Please enter a valid URL for live location');
                return;
            }

            // Find the order details before updating
            const order = orders.find(o => o._id === orderId);
            if (!order) {
                alert('Order not found');
                return;
            }

            await axiosInstance.put(`/api/deliveries/${orderId}`, { 
                status: 'In Transit',
                liveLocationUrl: liveLocationUrl.trim(),
                deliveryBoyNumber: deliveryBoyNumber.trim()
            });
            
            // Only bother the delivery boy with a WhatsApp tab if they actually
            // gave the customer something worth sharing (live location or a
            // contact number). Nothing to share -> nothing to send.
            const hasShareableInfo = liveLocationUrl.trim() || deliveryBoyNumber.trim();
            if (hasShareableInfo) {
                sendDeliveryStartWhatsApp(order, liveLocationUrl.trim(), deliveryBoyNumber.trim());
                alert('Delivery started! WhatsApp notification sent to customer.');
            } else {
                alert('Delivery started!');
            }
            setLiveLocationInputs(prev => {
                const updated = { ...prev };
                delete updated[orderId];
                return updated;
            });
            setLiveLocationErrors(prev => {
                const updated = { ...prev };
                delete updated[orderId];
                return updated;
            });
            setDeliveryBoyNumbers(prev => {
                const updated = { ...prev };
                delete updated[orderId];
                return updated;
            });
            fetchOrders();
        } catch (error) {
            console.error('Error starting delivery:', error);
            alert(error.response?.data?.message || 'Failed to start delivery');
        }
    };

    const sendDeliveryStartWhatsApp = (order, liveLocationUrl, deliveryBoyNumber) => {
        // Format phone number: add 974 prefix to 8-digit mobile
        const formattedPhone = `974${order.mobile}`;
        
        // Build WhatsApp message with professional template
        let message = `*━━━━━━━━━━━━━━━━━━━━*
*GREETINGS FROM AHLA MART*
*━━━━━━━━━━━━━━━━━━━━*

Dear ${order.customerName},

Your order is now on its way!

*ORDER DETAILS*
*Invoice Number:* ${order.invoiceNo}
*Amount:* QAR ${order.amount?.toLocaleString()}`;

        // Add delivery location with Google Maps link if available
        if (order.googleMapsUrl) {
            message += `
*Delivery Location:* ${order.area}
📍 ${order.googleMapsUrl}`;
        } else {
            message += `
*Delivery Location:* ${order.area}`;
        }

        // Add live location tracking link if available
        if (liveLocationUrl && liveLocationUrl.trim()) {
            message += `

📍 *Track your delivery in real-time:*
${liveLocationUrl}`;
        }

        // Add delivery boy contact number if available
        if (deliveryBoyNumber && deliveryBoyNumber.trim()) {
            const normalizedNumber = normalizeMobileNumber(deliveryBoyNumber.trim());
            message += `

📞 *Delivery Contact:* +974${normalizedNumber}`;
        }

        message += `

Our delivery executive will reach you shortly.

*━━━━━━━━━━━━━━━━━━━━*
*THANK YOU FOR CHOOSING AHLA MART!*
*━━━━━━━━━━━━━━━━━━━━*`;
        
        // Open WhatsApp with pre-filled message
        const whatsappUrl = `https://wa.me/${formattedPhone}?text=${encodeURIComponent(message)}`;
        window.open(whatsappUrl, '_blank');
    };

    const handleLiveLocationChange = (orderId, value) => {
        setLiveLocationInputs(prev => ({
            ...prev,
            [orderId]: value
        }));

        // Validate URL if not empty
        if (value.trim()) {
            const isValid = isValidUrl(value.trim());
            setLiveLocationErrors(prev => ({
                ...prev,
                [orderId]: isValid ? null : 'Please enter a valid URL (must start with http:// or https://)'
            }));
        } else {
            // Clear error if empty (optional field)
            setLiveLocationErrors(prev => ({
                ...prev,
                [orderId]: null
            }));
        }
    };

    const isValidUrl = (string) => {
        try {
            const url = new URL(string);
            return url.protocol === 'http:' || url.protocol === 'https:';
        } catch (_) {
            return false;
        }
    };

    const normalizeMobileNumber = (mobile) => {
        if (!mobile) return '';
        // Remove all non-numeric characters
        let cleaned = String(mobile).replace(/\D/g, '');
        // Remove 974 prefix if present
        if (cleaned.startsWith('974')) {
            cleaned = cleaned.substring(3);
        }
        return cleaned;
    };

    const handleDeliveryBoyNumberChange = (orderId, value) => {
        setDeliveryBoyNumbers(prev => ({
            ...prev,
            [orderId]: value
        }));
    };

    // Images open in the in-app viewer; PDFs open in a new tab (browsers render
    // PDFs natively there) — mirrors ChequeRegister's handleViewAttachment.
    const handleViewProofDocument = async (orderId, doc) => {
        try {
            const response = await axiosInstance.get(
                `/api/deliveries/${orderId}/proof/documents/${doc._id}/content`,
                { responseType: 'blob' }
            );
            const blob = new Blob([response.data], { type: response.data.type || doc.fileType });
            const url = URL.createObjectURL(blob);

            if (doc.fileType?.startsWith('image/')) {
                // revoke: true — this blob URL was created just for this view, and
                // the card behind it is a placeholder holding no reference, so it
                // is released on close — see the ImageViewer onClose below.
                setViewingImage({ url, title: doc.fileName, revoke: true });
            } else {
                window.open(url, '_blank');
                setTimeout(() => URL.revokeObjectURL(url), 10000);
            }
        } catch (error) {
            console.error('Error viewing proof document:', error);
            alert('Failed to open file');
        }
    };

    // Legacy single proof photo, fetched ONLY when the driver taps it. Nothing
    // is downloaded to render the card — these are full-resolution photos and
    // the phone is usually on mobile data.
    const handleViewLegacyProof = async (order) => {
        try {
            const response = await axiosInstance.get(
                `/api/deliveries/${order._id}/proof`,
                { responseType: 'blob' }
            );
            const url = URL.createObjectURL(response.data);
            setViewingImage({ url, title: `Proof - ${order.invoiceNo}`, revoke: true });
        } catch (error) {
            console.error('Error loading proof image:', error);
            alert('Failed to load proof image');
        }
    };

    const handleUploadProof = (order) => {
        setSelectedOrder(order);
        setShowProofModal(true);
    };

    // Closing the run. Proof is optional — deliberately, since some handovers
    // genuinely have nothing to photograph — so this warns rather than blocks,
    // and the admin row flags the result with "⚠ No proof".
    const handleMarkDelivered = async (order) => {
        const warning = hasProof(order)
            ? 'Mark this order as Delivered?'
            : 'No proof uploaded for this order.\n\nMark it Delivered anyway?';
        if (!window.confirm(warning)) return;
        try {
            await axiosInstance.put(`/api/deliveries/${order._id}`, { status: 'Delivered' });
            fetchOrders();
        } catch (error) {
            console.error('Error marking delivered:', error);
            alert(error.response?.data?.message || 'Failed to mark as Delivered');
        }
    };

    // A flag, not a stage: raise it or clear it at any point, on any status.
    const handleToggleInvoicePending = async (order) => {
        try {
            await axiosInstance.put(`/api/deliveries/${order._id}`, {
                invoicePending: !order.invoicePending
            });
            fetchOrders();
        } catch (error) {
            console.error('Error updating invoice-pending flag:', error);
            alert(error.response?.data?.message || 'Failed to update the invoice flag');
        }
    };

    const handleProofUploaded = () => {
        setShowProofModal(false);
        setSelectedOrder(null);
        fetchOrders();
    };

    if (loading && firstLoad) {
        return (
            <div className="my-orders-loading">
                <div className="spinner"></div>
                <p>Loading your orders...</p>
            </div>
        );
    }

    return (
        <div className="my-orders-container">
            {/* System-admin company/branch picker (null for other roles) */}
            {scopeSelector}

            {/* Header Actions */}
            <div className="my-orders-header">
                <div className="search-wrapper">
                    <HiOutlineSearch className="search-icon" />
                    <input
                        type="text"
                        placeholder="Search by customer, mobile, invoice, area, or remarks..."
                        value={searchTerm}
                        onChange={(e) => setSearchTerm(e.target.value)}
                        className="search-input"
                    />
                </div>
                <button className="refresh-btn" onClick={fetchOrders} disabled={loading}>
                    <HiOutlineRefresh /> Refresh
                </button>
            </div>

            {/* Status tabs — where the delivery is. Counts come from the server
                and cover every page, with the chips below already applied. */}
            <div className="status-filter-tabs">
                {[{ value: '', label: 'All', count: statusCounts.All },
                  ...STATUS_OPTIONS.map(s => ({ value: s, label: s, count: statusCounts[s] }))
                ].map((tab) => (
                    <button
                        key={tab.value || 'all'}
                        className={`filter-tab ${statusFilter === tab.value ? 'active' : ''}`}
                        onClick={() => handleStatusFilter(tab.value)}
                    >
                        {tab.label} ({tab.count ?? 0})
                    </button>
                ))}
            </div>

            {/* Indicator chips — independent of the tab above and of each other,
                so they narrow whichever tab is active rather than replacing it. */}
            <div className="indicator-filter-chips">
                <button
                    className={`filter-chip ${proofFilter ? 'active' : ''}`}
                    onClick={toggleProofFilter}
                >
                    📷 Has proof ({statusCounts.hasProof ?? 0})
                </button>
                <button
                    className={`filter-chip ${invoiceFilter ? 'active' : ''}`}
                    onClick={toggleInvoiceFilter}
                >
                    🧾 Invoice pending ({statusCounts.invoicePending ?? 0})
                </button>
            </div>

            {/* Orders List */}
            <div className="orders-list">
                {orders.length === 0 ? (
                    <div className="no-orders">
                        <p>{loading ? 'Loading orders...' : 'No orders found'}</p>
                    </div>
                ) : (
                    orders.map((order) => (
                        <div key={order._id} className="order-card">
                            <div className="order-card-header">
                                <div className="order-invoice">
                                    <strong>{order.invoiceNo}</strong>
                                </div>
                                <DeliveryIndicators delivery={order} />
                            </div>

                            <div className="order-card-body">
                                <div className="order-info-row">
                                    <span className="info-label">Customer:</span>
                                    <span className="info-value">{order.customerName}</span>
                                </div>
                                <div className="order-info-row">
                                    <span className="info-label">Mobile:</span>
                                    <span className="info-value">{order.mobile}</span>
                                </div>
                                <div className="order-info-row">
                                    <span className="info-label">Location:</span>
                                    <div className="info-value location-value">
                                        {order.area && <span>{order.area}</span>}
                                        {order.status === 'In Transit' && order.liveLocationUrl ? (
                                            <a
                                                href={order.liveLocationUrl}
                                                target="_blank"
                                                rel="noopener noreferrer"
                                                className="live-btn-small"
                                                title="Track Live Location"
                                            >
                                                <FaMapMarkerAlt size={12} className="pulse-icon-small" />
                                                <span>Live</span>
                                            </a>
                                        ) : order.googleMapsUrl ? (
                                            <a
                                                href={order.googleMapsUrl}
                                                target="_blank"
                                                rel="noopener noreferrer"
                                                className="map-btn-small"
                                                title="Open in Google Maps"
                                            >
                                                <FaMapMarkerAlt size={12} />
                                                <span>Map</span>
                                            </a>
                                        ) : null}
                                    </div>
                                </div>
                                <div className="order-info-row">
                                    <span className="info-label">Amount:</span>
                                    <span className="info-value amount">QAR {order.amount?.toLocaleString()}</span>
                                </div>
                                
                                {/* Payment info appears once there is proof, or once the
                                    delivery is closed — a fact about the order, not a status. */}
                                {(hasProof(order) || order.status === 'Delivered') && (
                                    <>
                                        <div className="order-info-row">
                                            <span className="info-label">Payment Type:</span>
                                            <span className="info-value payment-type">{order.paymentType || 'COD'}</span>
                                        </div>
                                        <div className="order-info-row">
                                            <span className="info-label">Amount Paid:</span>
                                            <span className="info-value amount-paid">QAR {(order.amountPaid || 0).toLocaleString()}</span>
                                        </div>
                                        <div className="order-info-row">
                                            <span className="info-label">Balance:</span>
                                            <span className={`info-value balance ${((order.amount || 0) - (order.amountPaid || 0)) === 0 ? 'balance-zero' : 'balance-pending'}`}>
                                                QAR {((order.amount || 0) - (order.amountPaid || 0)).toLocaleString()}
                                            </span>
                                        </div>
                                        {order.paymentNotes && (
                                            <div className="order-info-row">
                                                <span className="info-label">Payment Notes:</span>
                                                <span className="info-value">{order.paymentNotes}</span>
                                            </div>
                                        )}
                                    </>
                                )}
                                
                                {order.remarks && (
                                    <div className="order-info-row">
                                        <span className="info-label">Remarks:</span>
                                        <span className="info-value">{order.remarks}</span>
                                    </div>
                                )}
                            </div>

                            <div className="order-card-footer">
                                {order.status === 'Pending' && isDeliveryBoy && (
                                    <>
                                        <div className="live-location-input-group">
                                            <label className="live-location-label">
                                                <HiOutlineLocationMarker size={16} />
                                                Live Location Link (Optional)
                                            </label>
                                            <div className="input-with-validation">
                                                <input
                                                    type="url"
                                                    placeholder="Paste WhatsApp live location link here..."
                                                    value={liveLocationInputs[order._id] || ''}
                                                    onChange={(e) => handleLiveLocationChange(order._id, e.target.value)}
                                                    className={`live-location-input ${liveLocationErrors[order._id] ? 'input-error' : ''} ${liveLocationInputs[order._id] && !liveLocationErrors[order._id] ? 'input-valid' : ''}`}
                                                />
                                                {liveLocationInputs[order._id] && !liveLocationErrors[order._id] && (
                                                    <span className="input-checkmark">✓</span>
                                                )}
                                            </div>
                                            {liveLocationErrors[order._id] ? (
                                                <small className="live-location-error">
                                                    {liveLocationErrors[order._id]}
                                                </small>
                                            ) : (
                                                <small className="live-location-hint">
                                                    💡 Share your live location for better customer experience
                                                </small>
                                            )}
                                        </div>
                                        <div className="live-location-input-group">
                                            <label className="live-location-label">
                                                📞 Your Contact Number (Optional)
                                            </label>
                                            <input
                                                type="tel"
                                                placeholder="e.g., 55149207 or 97455149207"
                                                value={deliveryBoyNumbers[order._id] || ''}
                                                onChange={(e) => handleDeliveryBoyNumberChange(order._id, e.target.value)}
                                                className="live-location-input"
                                            />
                                            <small className="live-location-hint">
                                                💡 Share your number so customers can reach you
                                            </small>
                                        </div>
                                        <button 
                                            className="btn btn-primary btn-block"
                                            onClick={() => handleStartDelivery(order._id)}
                                        >
                                            <HiOutlineTruck /> Start Delivery
                                        </button>
                                    </>
                                )}
                                
                                {order.status === 'In Transit' && isDeliveryBoy && (
                                    <div className="shared-info-badges">
                                        {order.liveLocationUrl && (
                                            <span className="live-location-badge">
                                                📍 Live location shared
                                            </span>
                                        )}
                                        {order.deliveryBoyNumber && (
                                            <span className="live-location-badge">
                                                📞 Contact shared: +974{order.deliveryBoyNumber}
                                            </span>
                                        )}
                                    </div>
                                )}

                                {/* Upload Proof is available In Transit AND after Delivered —
                                    the backend never gates uploadProof on status (proof is its
                                    own fact, not a stage), so a driver who forgot a photo, or
                                    wants to add another, isn't locked out once closed. Mark
                                    Delivered only makes sense before the order is Delivered. */}
                                {(order.status === 'In Transit' || order.status === 'Delivered') && isDeliveryBoy && (
                                    <div className="order-action-row">
                                        <button
                                            className="btn btn-success"
                                            onClick={() => handleUploadProof(order)}
                                        >
                                            <HiOutlineCamera /> {order.status === 'Delivered' ? 'Add Proof / Note' : 'Upload Proof'}
                                        </button>
                                        {/* Ahlamart (and any branch added to RESTRICTED_ACTION_BRANCH_CODES):
                                            the office closes out delivery, not the driver. */}
                                        {order.status === 'In Transit' && !hideDeliveryBoyActions && (
                                            <button
                                                className="btn btn-primary"
                                                onClick={() => handleMarkDelivered(order)}
                                            >
                                                <HiOutlineCheckCircle /> Mark Delivered
                                            </button>
                                        )}
                                    </div>
                                )}

                                {/* A flag, not a stage — available on every status
                                    except a run that hasn't started yet. Hidden for
                                    RESTRICTED_ACTION_BRANCH_CODES — same reasoning as
                                    Mark Delivered above. */}
                                {order.status !== 'Pending' && isDeliveryBoy && !hideDeliveryBoyActions && (
                                    <button
                                        className={`btn btn-block btn-invoice-toggle ${order.invoicePending ? 'active' : ''}`}
                                        onClick={() => handleToggleInvoicePending(order)}
                                    >
                                        🧾 {order.invoicePending ? 'Invoice raised — clear flag' : 'Mark invoice pending'}
                                    </button>
                                )}

                                {hasProof(order) && (
                                    <div className="proof-info">
                                        <p className="proof-message">
                                            ✓ {order.status === 'Delivered' ? 'Delivered successfully' : 'Proof uploaded'}
                                        </p>
                                        {order.proofNote && (
                                            <p className="proof-note">Note: {order.proofNote}</p>
                                        )}
                                        {order.proofDocuments?.length > 0 ? (
                                            <div className="proof-doc-grid">
                                                {order.proofDocuments.map((doc) => (
                                                    <div
                                                        key={doc._id}
                                                        className="proof-doc-item"
                                                        onClick={() => handleViewProofDocument(order._id, doc)}
                                                        title={doc.fileName}
                                                    >
                                                        {doc.fileType?.startsWith('image/') ? (
                                                            <ProofPlaceholder
                                                                className="proof-doc-thumbnail"
                                                                title={`Tap to view ${doc.fileName}`}
                                                            />
                                                        ) : (
                                                            <div className="proof-doc-icon">
                                                                <HiOutlineEye size={22} />
                                                                <span>PDF</span>
                                                            </div>
                                                        )}
                                                        <span className="proof-doc-name">{doc.fileName}</span>
                                                    </div>
                                                ))}
                                            </div>
                                        ) : (
                                            <div className="proof-thumbnail-container">
                                                <ProofPlaceholder
                                                    className="proof-thumbnail"
                                                    onClick={() => handleViewLegacyProof(order)}
                                                />
                                                <span className="view-proof-text">Click to view full image</span>
                                            </div>
                                        )}
                                        {order.deliveredAt && (
                                            <p className="delivered-date">
                                                {new Date(order.deliveredAt).toLocaleString()}
                                            </p>
                                        )}
                                    </div>
                                )}
                            </div>
                        </div>
                    ))
                )}
            </div>

            {/* Pagination — the list is one page of `total`, so without this the
                rest of the queue was simply unreachable. */}
            {total > 0 && (
                <div className="mo-pagination">
                    <span className="mo-page-info">
                        Showing {(page - 1) * PAGE_SIZE + 1}–{Math.min(page * PAGE_SIZE, total)} of {total}
                    </span>
                    <div className="mo-page-controls">
                        <button
                            className="mo-page-btn"
                            onClick={() => setPage((p) => p - 1)}
                            disabled={page <= 1 || loading}
                        >
                            Previous
                        </button>
                        <span className="mo-page-current">Page {page} of {pageCount || 1}</span>
                        <button
                            className="mo-page-btn"
                            onClick={() => setPage((p) => p + 1)}
                            disabled={page >= pageCount || loading}
                        >
                            Next
                        </button>
                    </div>
                </div>
            )}

            {showProofModal && (
                <ProofUploadModal
                    order={selectedOrder}
                    onClose={() => {
                        setShowProofModal(false);
                        setSelectedOrder(null);
                    }}
                    onSuccess={handleProofUploaded}
                />
            )}

            {viewingImage && (
                <ImageViewer
                    imageUrl={viewingImage.url}
                    title={viewingImage.title}
                    onClose={() => {
                        if (viewingImage.revoke) URL.revokeObjectURL(viewingImage.url);
                        setViewingImage(null);
                    }}
                />
            )}
        </div>
    );
};

export default MyOrders;
